import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useMount } from 'react-use';
import { z } from 'zod';

import { AuthLayout } from '../../components/auth-layout/AuthLayout';
import { MfaStep } from '../../components/auth-layout/MfaStep';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/spinner';
import { COINIS_LOGO_DARK, GOOGLE_CLIENT_ID, PASSWORD_LOGIN_DISABLED } from '../../config';
import { useAutoLogin } from '../../context/AutoLoginContext';
import { authService, SsoProvider } from '../../services/auth';
import { authState } from '../../services/auth-state';
import { cn } from '../../utils/cn';
import { generateErrorMessages, isLockoutError } from '../../utils/showApiErrors';
import { SsoButton } from '../setup/components/SsoButton';
import { emailSchema } from '../setup/schemas/email';
import { ForgotPassword } from './components/ForgotPassword';
import { PasswordExpired } from './components/PasswordExpired';

enum LoginStep {
  CREDENTIALS = 'credentials',
  PASSWORD_EXPIRED = 'password-expired',
  TWO_FA_QR_CODE_INPUT = '2fa-qr_code_input',
  BACKUP_CODES = 'backup-codes',
  TWO_FA_CODE_INPUT = '2fa_code_input'
}

// Zod schema for login form validation
const loginFormSchema = z.object({
  ...emailSchema.shape,
  password: z.string().min(1, 'Please enter your password')
});

type LoginFormValues = z.infer<typeof loginFormSchema>;

export type AuthDataProps = {
  mfaLink: string | null;
  email?: string;
};

const parseStateRedirect = (stateParam?: string): { serviceRedirectUrl?: string; clientId?: string } => {
  try {
    const state = JSON.parse(stateParam ?? '{}');
    const authParams = state.auth_request_params;
    return {
      serviceRedirectUrl: state.service_redirect_url || authParams?.service_redirect_url || undefined,
      clientId: state.client_id || authParams?.client_id || undefined
    };
  } catch {
    return {};
  }
};

// Google and Facebook share this callback page; the provider travels in the
// OAuth `state` we generated. Absent means Google — redirects issued before
// Facebook sign-in shipped are still in flight during a deploy.
const parseStateProvider = (stateParam?: string): SsoProvider => {
  try {
    return JSON.parse(stateParam ?? '{}').provider === 'facebook' ? 'facebook' : 'google';
  } catch {
    return 'google';
  }
};

const buildRootRedirectSearch = (serviceRedirectUrl?: string, clientId?: string) => {
  const searchParams = new URLSearchParams();
  if (serviceRedirectUrl) {
    searchParams.set('service_redirect_url', serviceRedirectUrl);
  }

  if (clientId) {
    searchParams.set('client_id', clientId);
  }

  const search = searchParams.toString();
  return search ? `?${search}` : '';
};

export const Login = (): JSX.Element => {
  const [loading, setLoading] = useState(false);
  const [authData, setAuthData] = useState<AuthDataProps>({
    mfaLink: null,
    email: undefined
  });
  const [isMfaVerificationLoading, setMfaVerificationLoading] = useState(false);
  const [mfaError, setMfaError] = useState<string | Array<string> | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState<LoginStep>(LoginStep.CREDENTIALS);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | Array<string> | null>(null);
  const [ssoAuthLoading, setSsoAuthLoading] = useState<SsoProvider | null>(null);
  const [ssoCodeSubmitting, setSsoCodeSubmitting] = useState(false);

  // React Hook Form setup
  const loginForm = useForm<LoginFormValues>({
    defaultValues: {
      email: '',
      password: ''
    },
    resolver: zodResolver(loginFormSchema),
    mode: 'onTouched'
  });

  const emailValue = loginForm.watch('email');
  const isEmailValid = emailSchema.safeParse({ email: emailValue }).success;

  // One Tap automatic sign-in
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || autoLoginCtx.hasPayload() || params.get('code')) {
      return;
    }

    const scriptId = 'gsi-client';
    const initOneTap = () => {
      window.google?.accounts.id.initialize({
        // eslint-disable-next-line camelcase
        client_id: GOOGLE_CLIENT_ID,
        callback: (response: { credential: string }) => {
          void (async () => {
            try {
              setLoading(true);
              await authService.loginWithGoogleIdToken(
                response.credential,
                serviceRedirectUrl,
                clientId,
                allQueryParams
              );
              handleLoginSuccess();
            } catch {
              // One Tap failed silently — user can still use the button
            } finally {
              setLoading(false);
            }
          })();
        },
        // eslint-disable-next-line camelcase
        auto_select: true,
        // eslint-disable-next-line camelcase
        cancel_on_tap_outside: false
      });
      window.google?.accounts.id.prompt();
    };

    if (document.getElementById(scriptId)) {
      initOneTap();
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = initOneTap;
    document.head.appendChild(script);

    return () => {
      window.google?.accounts.id.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recover from the SSO redirect being cancelled (e.g. "Back" in the account chooser).
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setLoading(false);
        setSsoAuthLoading(null);
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const [isLocked, setIsLocked] = useState(false);

  const history = useHistory();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const serviceRedirectUrl = params.get('service_redirect_url') ?? undefined;
  const clientId = params.get('client_id') ?? undefined;

  // Collect all query params to store on user
  const allQueryParams = useMemo(() => {
    const result: Record<string, string> = {};
    params.forEach((value, key) => {
      result[key] = value;
    });
    return Object.keys(result).length > 0 ? result : undefined;
  }, [params]);

  const autoLoginCtx = useAutoLogin();
  const [autoLoginLoading, setAutoLoginLoading] = useState(autoLoginCtx.hasPayload());

  // Form submission handler
  const onLoginSubmit = async (values: LoginFormValues) => {
    setLoginError(null);
    setIsLocked(false);

    loginForm.clearErrors();

    await handleLoginAttempt(values);
  };

  // SSO auth handler
  const handleSsoAuth = async (provider: SsoProvider) => {
    setSsoAuthLoading(provider);
    try {
      await handleSsoLogin(provider);
    } finally {
      setSsoAuthLoading(null);
    }
  };

  const setLocation = (path: string) => {
    history.push(path);
  };

  // Where to send the user back to after a failed/cancelled SSO round trip:
  // the redirect target rides in the OAuth state, the URL params are the fallback.
  const redirectSearchFromState = (stateParam?: string) => {
    const { serviceRedirectUrl: stateRedirectUrl, clientId: stateClientId } = parseStateRedirect(stateParam);
    return buildRootRedirectSearch(stateRedirectUrl ?? serviceRedirectUrl, stateClientId ?? clientId);
  };

  useMount(() => {
    const mount = async () => {
      const code = params.get('code');

      // Handle auto-login payload from context
      const payload = autoLoginCtx.getAndClear();
      if (payload) {
        setAutoLoginLoading(true);
        switch (payload.type) {
          case 'credentials':
            handleLoginAttempt({ email: payload.email, password: payload.password });
            break;
          case 'google':
            handleSsoLogin('google');
            break;
          default:
            break;
        }

        return;
      }

      const stateParam = params.get('state') ?? undefined;
      // Back to /login without a code: the provider refused or the user
      // cancelled the consent dialog (?error=access_denied&...).
      const ssoError = params.get('error');
      if (!code && ssoError) {
        setLoginError(ssoError === 'access_denied' ? 'Sign-in was cancelled.' : 'Sign-in failed. Please try again.');
        setLocation(`/login${redirectSearchFromState(stateParam)}`);
        return;
      }

      if (code) {
        setLoading(true);
        setSsoCodeSubmitting(true);
        try {
          await authService.loginWithSsoCode(parseStateProvider(stateParam), code, stateParam);
          const { serviceRedirectUrl: stateRedirectUrl, clientId: stateClientId } = parseStateRedirect(stateParam);
          handleLoginSuccess(stateRedirectUrl, stateClientId);
          return;
        } catch (e) {
          setLoginError(generateErrorMessages(e as AxiosError));
          // Clear the outdated code from the URL, but keep the redirect target
          // so a retry still lands the user where they were headed.
          setLocation(`/login${redirectSearchFromState(stateParam)}`);
        } finally {
          setLoading(false);
          setSsoCodeSubmitting(false);
        }
      } else if (PASSWORD_LOGIN_DISABLED) {
        handleSsoLogin('google');
      }
    };

    mount();
  });

  const handleSsoLogin = async (provider: SsoProvider) => {
    setLoading(true);
    try {
      window.location.href = (
        await authService.getSsoAuthUrl(provider, serviceRedirectUrl, clientId, allQueryParams)
      ).url;
    } catch (e) {
      setLoginError(generateErrorMessages(e as AxiosError));
      setLoading(false);
    }
  };

  const handleLoginSuccess = (overrideServiceRedirectUrl?: string, overrideClientId?: string) => {
    authState.setLoggedIn(true);
    history.replace({
      pathname: '/',
      search: buildRootRedirectSearch(overrideServiceRedirectUrl ?? serviceRedirectUrl, overrideClientId ?? clientId)
    });
  };

  const handleLoginAttempt = async ({ email, password }: LoginFormValues): Promise<void> => {
    try {
      setLoading(true);
      setLoginError(null);
      setIsLocked(false);
      const loginData = await authService.login(email, password);
      if (loginData.loginCompleted) {
        handleLoginSuccess();
      } else if (loginData.mfaRequired) {
        setAuthData({ mfaLink: loginData.mfaLink, email });
        if (loginData.mfaLink) {
          setCurrentStep(LoginStep.TWO_FA_QR_CODE_INPUT);
        } else {
          setCurrentStep(LoginStep.TWO_FA_CODE_INPUT);
        }
      }
    } catch (e) {
      const error = e as AxiosError;
      if (isLockoutError(error)) {
        setIsLocked(true);
      }

      setLoginError(generateErrorMessages(error));
    } finally {
      setAutoLoginLoading(false);
      setLoading(false);
    }
  };

  const handleCodeInsert = async (code: string): Promise<void> => {
    try {
      setMfaVerificationLoading(true);
      setMfaError(null);
      const response = await authService.verifyMfa(code);
      if (response.passwordExpired) {
        setCurrentStep(LoginStep.PASSWORD_EXPIRED);
      } else {
        handleLoginSuccess();
      }
    } catch (e) {
      const errorMessages = generateErrorMessages(e as AxiosError);
      setMfaError(errorMessages);
      setLoginError(errorMessages);
    } finally {
      setMfaVerificationLoading(false);
    }
  };

  const returnErrorComponent = (): React.ReactNode => {
    const renderErrorMessages = (error: string | string[]) => {
      if (Array.isArray(error)) {
        return error.map((msg, index) => <div key={index}>{msg}</div>);
      }

      return error;
    };

    return !isLocked ? (
      <div className="text-loss text-sm font-medium">{renderErrorMessages(loginError!)}</div>
    ) : (
      <div className="flex flex-col gap-3">
        <Alert className="flex items-center border-brand-gold/30 bg-brand-gold-50">
          <AlertTriangle className="h-4 w-4 text-brand-gold" />
          <AlertDescription className="text-brand-orange">{renderErrorMessages(loginError!)}</AlertDescription>
        </Alert>
        <Button
          onClick={() => {
            setLocation('/reset-password');
          }}
          variant="outline"
          className="w-full h-10 border-brand-gold/30 text-brand-orange hover:bg-brand-gold-50"
        >
          Reset Password Now
        </Button>
      </div>
    );
  };

  // Show loading state during auto-login
  if (autoLoginLoading || ssoCodeSubmitting) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center justify-center flex-1 h-full">
          <Spinner size="lg" centered />
        </div>
      </AuthLayout>
    );
  }

  if (showForgotPassword) {
    return (
      <AuthLayout>
        <ForgotPassword onGoBack={() => setShowForgotPassword(false)} />
      </AuthLayout>
    );
  }

  const onBack = () => {
    setCurrentStep(LoginStep.CREDENTIALS);
    setMfaError(null);
    setLoginError(null);
  };

  return (
    <AuthLayout>
      {currentStep === LoginStep.PASSWORD_EXPIRED && <PasswordExpired onLoginSuccess={handleLoginSuccess} />}

      {currentStep === LoginStep.TWO_FA_QR_CODE_INPUT && (
        <MfaStep
          variant="qr"
          mfaLink={authData.mfaLink ?? ''}
          isLoading={isMfaVerificationLoading}
          error={mfaError}
          onComplete={handleCodeInsert}
          onBack={onBack}
        />
      )}

      {currentStep === LoginStep.TWO_FA_CODE_INPUT && (
        <MfaStep
          variant="code"
          email={authData.email}
          isLoading={isMfaVerificationLoading}
          error={mfaError}
          onComplete={handleCodeInsert}
          onBack={onBack}
        />
      )}

      {currentStep === LoginStep.CREDENTIALS && (
        <>
          <img src={COINIS_LOGO_DARK} className="h-7 w-auto" alt="Coinis" />

          <div className="flex flex-col gap-[11px]">
            <h1 className="font-onboarding-1 text-[2.35rem] font-bold leading-[1.04] tracking-[-0.025em] text-primary">
              Welcome back
            </h1>
            <p className="text-[15.5px] leading-[1.5] text-[var(--ink-2)]">Sign in to your Coinis workspace.</p>
          </div>

          <div className="flex flex-col gap-2.5">
            <SsoButton
              provider="google"
              onClick={() => handleSsoAuth('google')}
              loading={ssoAuthLoading === 'google'}
              disabled={!!ssoAuthLoading}
            />
            <SsoButton
              provider="facebook"
              onClick={() => handleSsoAuth('facebook')}
              loading={ssoAuthLoading === 'facebook'}
              disabled={!!ssoAuthLoading}
            />
          </div>

          <div className="flex items-center gap-[14px] text-[13px] font-medium text-[var(--ink-3)]">
            <span className="h-px flex-1 bg-[var(--line)]" />
            or
            <span className="h-px flex-1 bg-[var(--line)]" />
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void loginForm.handleSubmit(onLoginSubmit)(e);
            }}
            className="flex flex-col gap-[14px]"
            noValidate
          >
            <div>
              <Input
                id="login-email"
                type="email"
                autoComplete="email"
                placeholder="you@yourbrand.com"
                aria-invalid={!!loginForm.formState.errors.email}
                {...loginForm.register('email')}
              />
              {loginForm.formState.errors.email && (
                <p className="mt-1.5 text-xs text-loss">{loginForm.formState.errors.email.message}</p>
              )}
            </div>

            <div
              className={cn(
                'overflow-hidden transition-all duration-300 ease-out',
                isEmailValid ? 'max-h-[120px] opacity-100' : 'max-h-0 opacity-0'
              )}
            >
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label htmlFor="login-password" className="text-[13px] font-medium text-[var(--ink)]">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-[13px] font-semibold text-brand-blue hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className="pr-11"
                    aria-invalid={!!loginForm.formState.errors.password}
                    {...loginForm.register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)] transition hover:text-[var(--ink)]"
                  >
                    {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="mt-1.5 text-xs text-loss">{loginForm.formState.errors.password.message}</p>
                )}
              </div>
            </div>

            {loginError && returnErrorComponent()}

            <button
              type="submit"
              disabled={loading || !isEmailValid}
              className="btn-cta-orange mt-2 flex h-12 w-full items-center justify-center rounded-lg text-[15px] font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  <span>Signing in...</span>
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <p className="text-center text-[13.5px] font-medium text-[var(--ink-2)]">
            Don&apos;t have an account?{' '}
            <a
              href={`/register${window.location.search}`}
              className="cursor-pointer font-bold text-primary hover:underline"
            >
              Sign up
            </a>
          </p>
          <p className="text-center text-[11.5px] leading-[1.5] text-[var(--ink-3)]">
            By creating an account, you agree to our{' '}
            <a
              href="https://coinis.com/terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer text-[var(--ink-2)] underline underline-offset-2"
            >
              Terms
            </a>{' '}
            and{' '}
            <a
              href="https://coinis.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer text-[var(--ink-2)] underline underline-offset-2"
            >
              Privacy Policy
            </a>
            .
          </p>
        </>
      )}
    </AuthLayout>
  );
};
